import { useGT } from 'gt-react';
import { useNavigate } from '@tanstack/react-router';
import type { StageChangesResponse } from '@erp/rpc';
import { useToast } from '@/context/ToastContext';

/**
 * The success/error handling every "stage a new review" dialog shares: a toast
 * with the staged count, then close and land on the new review (or, from the
 * catalog, on the reviews list); a toast with the server's message on failure.
 */
export function useStagedChangeSetFeedback({
  landOn,
  onDone,
}: {
  landOn: 'review' | 'reviews';
  /** Runs after a successful stage, before navigating (close the dialog). */
  onDone: () => void;
}) {
  const gt = useGT();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const onSuccess = (res: StageChangesResponse) => {
    showToast({
      title: gt('Change set staged', {
        context: 'toast title after staging products into a change set',
      }),
      message: gt('{count} products staged for review.', {
        context: 'toast message after staging products',
        count: String(res.stagedCount),
      }),
      color: 'success',
      icon: 'check',
    });
    onDone();
    void (landOn === 'review'
      ? navigate({
          to: '/item-master/reviews/$changeSetId',
          params: { changeSetId: res.changeSetId },
        })
      : navigate({ to: '/item-master/reviews' }));
  };

  const onError = (error: Error) =>
    showToast({
      title: gt('Error', { context: 'stage change set error toast title' }),
      message:
        error.message ||
        gt('Failed to stage the change set.', {
          context: 'fallback error when staging a change set fails',
        }),
      color: 'error',
      icon: 'x',
    });

  return { onSuccess, onError };
}
